// ============================================
// BEHAVIORAL CHART DESIGN STANDARD
// ============================================
// Centralized constants for InlineBehavioralChart component
// Ensures consistent visual appearance across all chart instances
// ============================================

import { TRAIT_COLORS } from './trait-colors';

/**
 * Chart Aspect Ratios
 * Wide mode: 3:2 landscape ratio for desktop displays
 * Compact mode: Fixed dimensions with 90° rotation
 */
export const CHART_ASPECT_RATIOS = {
  wide: '3/2',
  compact: null, // Uses fixed dimensions instead
} as const;

/**
 * Chart Container Dimensions
 * Wide mode: 50% width with 3:2 aspect ratio (training pages)
 * Card mode: Full width with 3:2 aspect ratio (org chart cards)
 * Compact mode: Full width, fixed 96px height (candidate table)
 */
export const CHART_CONTAINER = {
  wide: {
    width: 'w-1/2',
    height: null, // Controlled by aspect ratio
    aspectRatio: '3/2',
  },
  card: {
    width: 'w-full',
    height: null, // Controlled by aspect ratio
    aspectRatio: '3/2',
  },
  compact: {
    width: 'w-full',
    height: 'h-24',
    aspectRatio: null,
  },
  'mobile-card': {
    width: 'w-full',
    height: 'h-full',
    aspectRatio: null,
  },
} as const;

/**
 * Chart Content Dimensions
 * The actual chart rendering area (before rotation in compact mode)
 */
export const CHART_CONTENT = {
  wide: {
    width: 'w-full',
    height: 'h-full',
    rotation: 'none',
  },
  compact: {
    width: 'w-14',  // 56px - visual height after 90° rotation
    height: 'h-36', // 144px - visual width after 90° rotation (~80% wider spread)
    rotation: 'rotate(90deg)',
  },
  'mobile-card': {
    width: 'w-full',
    height: 'h-full',
    rotation: 'none',
  },
} as const;

/**
 * Dot Sizes (in pixels)
 * ABCD trait dots: Wide/Card (7.8px radius), Compact (4px radius)
 * Style dot: Matches ABCD dot diameter (radius × 2)
 */
export const CHART_DOT_SIZES = {
  wide: {
    traitDots: 7.8, // Radius in pixels for ABCD dots (training pages)
    objectivityDot: {
      width: 'w-[15.6px]', // Matches trait dot diameter (7.8px × 2)
      height: 'h-[15.6px]',
    },
  },
  card: {
    traitDots: 7.8, // Radius in pixels for ABCD dots (org chart cards) - matches wide
    objectivityDot: {
      width: 'w-[15.6px]', // Matches trait dot diameter (7.8px × 2)
      height: 'h-[15.6px]',
    },
  },
  compact: {
    traitDots: 3.4,
    objectivityDot: {
      width: 'w-[6.8px]',
      height: 'h-[6.8px]',
    },
  },
  'mobile-card': {
    traitDots: 3.4,
    objectivityDot: {
      width: 'w-[6.8px]',
      height: 'h-[6.8px]',
    },
  },
} as const;

/**
 * Line Styles
 * Connecting lines: 2px solid stroke
 * Reference line (50th percentile): 1px dashed
 */
export const CHART_LINE_STYLES = {
  connecting: {
    strokeWidth: 2,
    type: 'monotone' as const,
  },
  reference: {
    strokeWidth: 1,
    strokeDasharray: '3 3',
    stroke: '#94a3b8', // slate-400
  },
} as const;

/**
 * Chart Margins (in pixels)
 * Space between chart edges and container boundaries
 */
export const CHART_MARGINS = {
  wide: {
    top: 10,
    right: 10,
    bottom: 10,
    left: 10,
  },
  card: {
    top: 12,
    right: 8,
    bottom: 8,
    left: 8,
  },
  compact: {
    top: 5,
    right: 5,
    bottom: 5,
    left: 5,
  },
  'mobile-card': {
    top: 4,
    right: 4,
    bottom: 2,
    left: 4,
  },
} as const;

/**
 * Trait Colors (DCTOS Framework)
 * Re-exported from trait-colors.ts for convenience
 * D=Red/Coral, C=Gold, T=Sky Blue, O=Mint, S=Gray
 */
export const CHART_COLORS = {
  dominance: TRAIT_COLORS.dominance,      // D (Drive): Red/Coral (#FF6B6B)
  extraversion: TRAIT_COLORS.extraversion, // C (Connection): Gold (#FFD93D)
  patience: TRAIT_COLORS.patience,         // T (Task): Sky Blue (#6DD5FA)
  formality: TRAIT_COLORS.formality,       // O (Order): Mint (#34D399)
  objectivity: TRAIT_COLORS.objectivity,   // S (Style): Gray (#9CA3AF)
  flagged: '#ef4444',                      // Red for flagged deviations
} as const;

/**
 * Style Separator Line
 * Visual divider between DCTO traits and S (Style/Style)
 */
export const OBJECTIVITY_SEPARATOR = {
  borderClass: 'border-t border-border',
  thickness: '1px',
} as const;

/**
 * Bar Chart Constants
 * Used by BehavioralChart, MobileBehavioralChart, SimplifiedBehavioralChart
 * Horizontal bar charts showing trait percentiles
 */
export const BAR_CHART = {
  barRadius: [0, 4, 4, 0] as [number, number, number, number], // Right side rounded
  minPointSize: 5, // Minimum visible bar size for very low values
  categoryGap: {
    compact: '20%',
    default: '30%',
  },
  margins: {
    mobile: { top: 15, right: 25, left: 60, bottom: 15 },
    desktop: { top: 15, right: 25, left: 80, bottom: 15 },
    compact: { top: 5, right: 15, left: 70, bottom: 5 },
    default: { top: 15, right: 25, left: 90, bottom: 15 },
  },
} as const;

/**
 * Scatter Chart Constants
 * Used by DesktopBehavioralChart
 * Scatter plot with connecting lines showing trait distribution
 */
export const SCATTER_CHART = {
  dotSize: 8, // Dot radius in pixels
  lineWidth: 2, // Connecting line stroke width
  margins: {
    compact: { top: 8, right: 15, left: 30, bottom: 8 },
    default: { top: 15, right: 20, left: 50, bottom: 15 },
  },
  tickFontSize: {
    compact: 9,
    default: 10,
  },
  labelFontSize: {
    compact: 12,
    default: 12,
  },
} as const;

/**
 * Usage Guidelines:
 * 
 * Wide Mode:
 * - Use for training pages and detailed analysis
 * - 50% width with 3:2 aspect ratio
 * - Large dots (7.8px) for clear visibility
 * 
 * Card Mode:
 * - Use for org chart cards and medium displays
 * - Full width with 3:2 aspect ratio
 * - Large dots (7.8px) matching wide mode for consistency
 * 
 * Compact Mode:
 * - Use for candidate table inline previews
 * - Full width, fixed 96px height
 * - Small dots (4px) for dense display
 * 
 * Dot Size Calculation:
 * - Wide/Card mode: 7.8px radius for full-size charts
 * - Compact mode: 4px radius for inline previews
 */
